import React, { useEffect, useState } from 'react';
import { GlTemeljniceTab } from './glTemeljniceTab';
import { GlBilancaTab } from './glBilancaTab';
import { GlPromjeneTab } from './glPromjeneTab';
import { GlUparivanjeTab } from './glUparivanjeTab';
import { GlGlavnaKnjiga, GkAlat } from './glGlavnaKnjiga';
import { api, ApiError, downloadFile } from '../shared/api';
import { formatMoney, formatNumber, formatDate } from '../shared/formatting';

type Locale = 'hr' | 'en';

type Company = { code: string; name: string; rows: number; date_min: string | null; date_max: string | null };
type Meta = { companies: Company[]; default_code: string };

type Filters = {
    company: string; datum_po: 'razdoblje' | 'knjizenje'; from: string; to: string;
    konto_od: string; konto_do: string; partner: string; mtp: string; vrsta: string; q: string; dopune: boolean;
};

type Totals = { n: number; duguje: number; potrazuje: number; saldo: number };
type KontoRow = Totals & { konto: string; naziv: string | null };
type Line = {
    id: number; konto: string; naziv_konta: string | null; datum_razdoblja: string | null; datum_knjizenja: string | null;
    temeljnica: string | null; vrsta: string | null; dokument: string | null; sifra_partnera: string | null; partner: string | null;
    sifra_mtp: string | null; mtp: string | null; opis: string | null; duguje: number; potrazuje: number; saldo: number; izvor: string | null;
};
type LinesResp = { totals: Totals; by_konto: KontoRow[]; page: number; page_size: number; rows: Line[]; vrste: string[] };

function query(f: Filters, page?: number): string {
    const qs = new URLSearchParams();
    Object.entries(f).forEach(([k, v]) => {
        if (typeof v === 'boolean') { if (v) qs.set(k, '1'); } else if (v !== '') qs.set(k, v);
    });
    if (page) qs.set('page', String(page));
    return qs.toString();
}

/**
 * Modul 10.1, Analitika glavne knjige. Isti pregled koji kontroling trazi od
 * RIS-a za Faros (konto od-do, datum razdoblja uz datum knjizenja), ali nad
 * dnevnim ANAL_GK uvozom, pa ne traje satima. Filtri se primjenjuju gumbom,
 * XLS izvoz uzima iste filtre i sve retke.
 */
function AnalitikaTab({ locale, token, company }: { locale: Locale; token: string | null; company: string }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);

    const [meta, setMeta] = useState<Meta | null>(null);
    const [draft, setDraft] = useState<Filters | null>(null);
    const [applied, setApplied] = useState<Filters | null>(null);
    const [page, setPage] = useState(1);
    const [data, setData] = useState<LinesResp | null>(null);
    const [loading, setLoading] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [showKonta, setShowKonta] = useState(true);

    const defaults = (c: Company | undefined, code: string): Filters => {
        const max = c?.date_max ?? new Date().toISOString().slice(0, 10);
        return {
            company: code, datum_po: 'razdoblje', from: `${max.slice(0, 4)}-01-01`, to: max,
            konto_od: '', konto_do: '', partner: '', mtp: '', vrsta: '', q: '', dopune: false,
        };
    };

    useEffect(() => {
        api<Meta>('/gl-analitika/meta', token)
            .then((m) => {
                setMeta(m);
                const code = company || m.default_code;
                const f = defaults(m.companies.find((c) => c.code === code), code);
                setDraft(f);
                setApplied(f);
            })
            .catch((e) => setError(e instanceof ApiError ? e.message : t('Učitavanje nije uspjelo.', 'Loading failed.')));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    useEffect(() => {
        if (!meta || !draft || draft.company === company) return;
        const c = meta.companies.find((x) => x.code === company);
        const f = defaults(c, company);
        setDraft(f);
        apply(f);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [company, meta]);

    useEffect(() => {
        if (!applied) return;
        setLoading(true);
        setError(null);
        api<LinesResp>(`/gl-analitika/lines?${query(applied, page)}`, token)
            .then(setData)
            .catch((e) => setError(e instanceof ApiError ? e.message : t('Učitavanje nije uspjelo.', 'Loading failed.')))
            .finally(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [applied, page, token]);

    const set = <K extends keyof Filters>(k: K, v: Filters[K]) => setDraft((d) => (d ? { ...d, [k]: v } : d));
    const apply = (f: Filters | null = draft) => { if (f) { setPage(1); setApplied({ ...f }); } };

    const drillKonto = (konto: string) => {
        if (!draft) return;
        const f = { ...draft, konto_od: konto, konto_do: konto };
        setDraft(f);
        apply(f);
    };

    async function exportXlsx() {
        if (!applied) return;
        setExporting(true);
        try {
            await downloadFile(`/gl-analitika/export.xlsx?${query(applied)}`, token,
                `analitika-gk-${applied.company}-${applied.from || 'pocetak'}-${applied.to || 'kraj'}.xlsx`);
        } catch (e) {
            setError(e instanceof Error ? e.message : t('Izvoz nije uspio.', 'Export failed.'));
        } finally {
            setExporting(false);
        }
    }

    const m = (v: number) => formatMoney(v, locale);
    const pages = data ? Math.max(1, Math.ceil(data.totals.n / data.page_size)) : 1;
    const dirty = draft && applied && JSON.stringify(draft) !== JSON.stringify(applied);
    const num: React.CSSProperties = { textAlign: 'right', whiteSpace: 'nowrap' };

    return (
        <div>
            {draft ? (
                <form onSubmit={(e) => { e.preventDefault(); apply(); }} style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: '0.8rem' }}>
                    <label>{t('Datum po', 'Date by')}<br />
                        <select value={draft.datum_po} onChange={(e) => set('datum_po', e.target.value as Filters['datum_po'])}>
                            <option value="razdoblje">{t('Datum razdoblja', 'Period date')}</option>
                            <option value="knjizenje">{t('Datum knjiženja', 'Posting date')}</option>
                        </select>
                    </label>
                    <label>{t('Od', 'From')}<br /><input type="date" value={draft.from} onChange={(e) => set('from', e.target.value)} /></label>
                    <label>{t('Do', 'To')}<br /><input type="date" value={draft.to} onChange={(e) => set('to', e.target.value)} /></label>
                    <label>{t('Konto od', 'Account from')}<br /><input value={draft.konto_od} onChange={(e) => set('konto_od', e.target.value)} placeholder="4" style={{ width: 90 }} /></label>
                    <label>{t('Konto do', 'Account to')}<br /><input value={draft.konto_do} onChange={(e) => set('konto_do', e.target.value)} placeholder="7" style={{ width: 90 }} /></label>
                    <label>{t('Partner', 'Partner')}<br /><input value={draft.partner} onChange={(e) => set('partner', e.target.value)} placeholder={t('naziv ili šifra', 'name or code')} style={{ width: 150 }} /></label>
                    <label>MTP<br /><input value={draft.mtp} onChange={(e) => set('mtp', e.target.value)} placeholder={t('naziv ili šifra', 'name or code')} style={{ width: 120 }} /></label>
                    <label>{t('Vrsta dokumenta', 'Document type')}<br />
                        <select value={draft.vrsta} onChange={(e) => set('vrsta', e.target.value)}>
                            <option value="">{t('sve', 'all')}</option>
                            {(data?.vrste ?? []).map((v) => <option key={v} value={v}>{v}</option>)}
                        </select>
                    </label>
                    <label>{t('Opis / broj', 'Text / number')}<br /><input value={draft.q} onChange={(e) => set('q', e.target.value)} style={{ width: 150 }} /></label>
                    <label title={t('Reci koje je BIS dodao (imputed, RDG dopuna); nisu u Farosu.', 'Rows added by BIS (imputed, RDG top-up); not in Faros.')}>
                        <input type="checkbox" checked={draft.dopune} onChange={(e) => set('dopune', e.target.checked)} /> {t('uključi BIS dopune', 'include BIS top-ups')}
                    </label>
                    <button type="submit" className={dirty ? 'compact' : 'secondary compact'}>{t('Prikaži', 'Show')}</button>
                    <button type="button" className="secondary compact" onClick={() => { const c = meta?.companies.find((x) => x.code === draft.company); const f = defaults(c, draft.company); setDraft(f); apply(f); }}>{t('Očisti', 'Reset')}</button>
                    <button type="button" className="compact" onClick={exportXlsx} disabled={exporting || !data || data.totals.n === 0 || !!dirty}
                        title={dirty ? t('Najprije primijeni filtre (Prikaži).', 'Apply filters first (Show).') : ''}>
                        {exporting ? t('Izvozim…', 'Exporting…') : t('Izvoz XLS', 'Export XLS')}
                    </button>
                    {loading ? <span className="muted">{t('Učitavam…', 'Loading…')}</span> : null}
                </form>
            ) : null}

            {error ? <p style={{ color: '#b91c1c' }}>{error}</p> : null}

            {data ? (
                <>
                    <p style={{ margin: '0.4rem 0 0.8rem' }}>
                        <strong>{formatNumber(data.totals.n, locale)}</strong> {t('redaka', 'rows')}
                        {' · '}{t('Duguje', 'Debit')} <strong>{m(data.totals.duguje)}</strong>
                        {' · '}{t('Potražuje', 'Credit')} <strong>{m(data.totals.potrazuje)}</strong>
                        {' · '}{t('Saldo', 'Balance')} <strong>{m(data.totals.saldo)}</strong>
                    </p>

                    <h3 style={{ cursor: 'pointer', margin: '0.6rem 0' }} onClick={() => setShowKonta((s) => !s)}>
                        {showKonta ? '▾' : '▸'} {t('Zbroj po kontu', 'Totals per account')} ({data.by_konto.length})
                    </h3>
                    {showKonta ? (
                        <div style={{ maxHeight: 340, overflow: 'auto', marginBottom: '1rem' }}>
                            <table className="data-table compact">
                                <thead><tr>
                                    <th>{t('Konto', 'Account')}</th><th>{t('Naziv', 'Name')}</th><th style={num}>{t('Redaka', 'Rows')}</th>
                                    <th style={num}>{t('Duguje', 'Debit')}</th><th style={num}>{t('Potražuje', 'Credit')}</th><th style={num}>{t('Saldo', 'Balance')}</th>
                                </tr></thead>
                                <tbody>
                                    {data.by_konto.map((k) => (
                                        <tr key={k.konto} style={{ cursor: 'pointer' }} onClick={() => drillKonto(k.konto)} title={t('Prikaži samo ovaj konto', 'Show this account only')}>
                                            <td>{k.konto}</td><td>{k.naziv ?? ''}</td><td style={num}>{formatNumber(k.n, locale)}</td>
                                            <td style={num}>{m(k.duguje)}</td><td style={num}>{m(k.potrazuje)}</td><td style={num}>{m(k.saldo)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : null}

                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', margin: '0.4rem 0' }}>
                        <h3 style={{ margin: 0 }}>{t('Knjiženja', 'Postings')}</h3>
                        <span className="muted">{t('stranica', 'page')} {data.page} / {pages}</span>
                        <button type="button" className="secondary compact" disabled={page <= 1 || loading} onClick={() => setPage((p) => p - 1)}>‹</button>
                        <button type="button" className="secondary compact" disabled={page >= pages || loading} onClick={() => setPage((p) => p + 1)}>›</button>
                    </div>
                    <div style={{ overflow: 'auto' }}>
                        <table className="data-table compact">
                            <thead><tr>
                                <th>{t('Konto', 'Account')}</th><th>{t('Datum razdoblja', 'Period date')}</th><th>{t('Datum knjiženja', 'Posting date')}</th>
                                <th>{t('Temeljnica', 'Journal')}</th><th>{t('Vrsta dok.', 'Doc type')}</th><th>{t('Broj dok.', 'Doc no.')}</th>
                                <th>{t('Partner', 'Partner')}</th><th>MTP</th><th>{t('Opis', 'Description')}</th>
                                <th style={num}>{t('Duguje', 'Debit')}</th><th style={num}>{t('Potražuje', 'Credit')}</th>
                            </tr></thead>
                            <tbody>
                                {data.rows.map((r) => (
                                    <tr key={r.id}>
                                        <td title={r.naziv_konta ?? ''}>{r.konto}</td>
                                        <td>{r.datum_razdoblja ? formatDate(r.datum_razdoblja, locale) : ''}</td>
                                        <td>{r.datum_knjizenja ? formatDate(r.datum_knjizenja, locale) : ''}</td>
                                        <td>{r.temeljnica ?? ''}</td><td>{r.vrsta ?? ''}</td><td>{r.dokument ?? ''}</td>
                                        <td title={r.sifra_partnera ?? ''}>{r.partner ?? ''}</td>
                                        <td title={r.sifra_mtp ?? ''}>{r.mtp ?? ''}</td>
                                        <td>{r.opis ?? ''}</td>
                                        <td style={num}>{r.duguje ? m(r.duguje) : ''}</td><td style={num}>{r.potrazuje ? m(r.potrazuje) : ''}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <p className="muted" style={{ marginTop: '0.8rem' }}>
                        {t('Datum dokumenta i broj tuđeg dokumenta ANAL_GK izvoz iz Farosa ne sadrži; dodaju se kad ih RIS uključi u izvoz.', 'Document date and external document number are not in the Faros ANAL_GK export; they will be added once RIS includes them.')}
                    </p>
                </>
            ) : null}
        </div>
    );
}

type Tab = 'analitika' | 'temeljnice' | 'bilanca' | 'promjene' | 'uparivanje';

/**
 * Modul 10.1 ljuska: zadani prikaz je Glavna knjiga po kontu preko cijelog
 * zaslona. Stari pregledi ostaju nepromijenjeni pod izbornikom Alati: Analitika
 * (filtri i XLS za kontroling), Temeljnice i nacrti, Probna bilanca (utjecaj
 * nacrta), Promjene uvoza, Uparivanje otvorenih stavki.
 */
export function GlAnalitikaModule({ locale, token, onBack }: { locale: Locale; token: string | null; onBack?: () => void }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const alati: GkAlat[] = [
        { key: 'analitika', label: t('Analitika s filtrima', 'Filtered analytics') },
        { key: 'temeljnice', label: t('Temeljnice i nacrti', 'Journals and drafts') },
        { key: 'bilanca', label: t('Probna bilanca', 'Trial balance') },
        { key: 'promjene', label: t('Promjene uvoza', 'Import changes') },
        { key: 'uparivanje', label: t('Uparivanje', 'Matching') },
    ];
    const renderAlat = (key: string, company: string, godina: number, goTo: (k: string) => void) => {
        const tab = key as Tab;
        return tab === 'analitika' ? <AnalitikaTab locale={locale} token={token} company={company} />
            : tab === 'temeljnice' ? <GlTemeljniceTab locale={locale} token={token} company={company} godina={godina} />
                : tab === 'bilanca' ? <GlBilancaTab locale={locale} token={token} company={company} godina={godina} />
                    : tab === 'promjene' ? <GlPromjeneTab locale={locale} token={token} company={company} />
                        : tab === 'uparivanje' ? <GlUparivanjeTab locale={locale} token={token} company={company} godina={godina} onOpenNacrti={() => goTo('temeljnice')} />
                            : null;
    };
    return <GlGlavnaKnjiga locale={locale} token={token} onBack={onBack} alati={alati} renderAlat={renderAlat} />;
}
